import { useCallback } from 'react';
import { useTracking } from 'react-tracking';

import { Text, Grid } from '@simplywallst/ui-core';

import { formatterCurrency } from '@/utilities/format';

import type { WatchlistItem } from '@/hooks/useWatchlist';

import { useFairValueContext } from './Context';
import { UnsetFairValue, EditIcon } from './styled';

interface Props {
  watchlistItem: WatchlistItem;
}

const SetFairValue = ({ watchlistItem }: Props) => {
  const { trackEvent } = useTracking();
  const { actions } = useFairValueContext();

  const { company, fairValue } = watchlistItem;
  const { npvPerShare } = company.analysisValue;
  const { trading_item_currency_iso: companyCurrencyISO } =
    company.data.currencyInfo;

  const handleOpen = useCallback(() => {
    actions.openFairvalueModal(watchlistItem);
    trackEvent({
      type: 'track',
      subject: 'SetFairValue',
      companyId: watchlistItem.companyId,
      action: 'click',
    });
  }, [trackEvent, actions, watchlistItem]);

  if (fairValue === null) {
    return (
      <UnsetFairValue
        as="button"
        onClick={handleOpen}
        aria-label="Edit fair value"
        data-cy-id="unset-fair-value"
        templateRows="auto auto"
        placeItems="start"
        alignItems="center"
      >
        <Grid templateColumns="min-content min-content">
          <EditIcon />
          <Text as="span" typography="base" color="brand01">
            set
          </Text>
        </Grid>
        <Text
          as="span"
          typography="tinyCondensed"
          color="whiteRGBA"
          opacity={0.3}
        >
          FV {formatterCurrency(npvPerShare, companyCurrencyISO)}
        </Text>
      </UnsetFairValue>
    );
  }

  return (
    <Grid
      as="button"
      onClick={handleOpen}
      aria-label="Edit fair value"
      data-cy-id="set-fair-value"
      gap="8px"
      templateColumns="auto auto"
      alignItems="center"
      placeContent="start"
      placeContentSm="center"
    >
      <Text
        as="span"
        typography="tinyCondensed"
        color="whiteRGBA"
        opacity={0.3}
      >
        FV
      </Text>
      <Text as="span" typography="xSmall" color="brand01">
        {formatterCurrency(fairValue, companyCurrencyISO)}
      </Text>
    </Grid>
  );
};

export default SetFairValue;
